import { Popup as PopupOld } from '@approvalmax/ui';
import { ConditionWrapper, Grid, Popup } from '@approvalmax/ui/src/components';
import { stringHelpers } from '@approvalmax/utils';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import uniqWith from 'lodash/uniqWith';
import { backend, domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { useCurrentUser } from 'modules/utils';
import moment from 'moment';
import { getCanCreateContact } from 'pages/requestForm/selectors/requestSelectors';
import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMount, usePrevious, useUnmount } from 'react-use';
import { CreateNetSuiteVendor } from 'shared/data/webApp/v1';
import { emailToSupplierHelpers } from 'shared/helpers';
import {
    useNetSuiteAttachmentsFieldSettings,
    useNetSuiteExpensesFieldSettings,
    useNetSuiteItemsFieldSettings,
    useNetSuitePurchaseOrderFieldSettings,
    useUserBetaFeatures,
} from 'shared/hooks';

import { AttachmentsSection, RequesterInstructionSection } from '../../../components';
import FileDropProvider from '../../../components/FileDropProvider';
import { getNetSuiteContext } from '../../../selectors/netsuite/netSuiteSelectors';
import { getContext } from '../../../selectors/pageSelectors';
import ActionsBlock from '../../form/ActionsBlock/ActionsBlock';
import FilesSection from '../../form/FilesSection';
import {
    NetSuiteRequestFooterLegacy,
    NetSuiteRequestItemsSummary,
    NetSuiteVendorCreationPopupContent,
} from '../components';
import NetSuiteEmailToSupplier from '../components/NetSuiteEmailToSupplier/NetSuiteEmailToSupplier';
import NetSuiteExpenses from '../components/NetSuiteExpenses/NetSuiteExpenses';
import NetSuiteItems from '../components/NetSuiteItems/NetSuiteItems';
import NetSuiteVendorCreationPopup from '../components/NetSuiteVendorCreationPopup/NetSuiteVendorCreationPopup';
import { convertCustomFieldsToState, getCustomFieldsTransfer, mapAddress } from '../helpers';
import {
    useCalculatedAmount,
    useCustomFields,
    useEmailToSupplier,
    useExchangeRates,
    useExpenseItems,
    useItemLines,
    useResetPurchaseOrderFormState,
    useUploadAttachments,
    useVendorData,
} from '../hooks';
import {
    getExpenseAndItemLineTransfers,
    isFieldMandatory,
    isFieldVisible,
    isFutureDate,
} from '../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { Content, ContentInner, Root, Toolbar, ToolbarText } from '../NetSuiteRequestForm/NetSuiteRequestForm.styles';
import {
    netSuiteCustomFieldsState,
    netSuiteExpenseItemsState,
    netSuiteItemLineNumberState,
    netSuiteItemLinesState,
    netSuiteShowErrorsState,
} from '../resources/states';
import { FieldsSection, ShippingDetailsSection } from './components';
import { getPurchaseOrderValidationErrors } from './NetSuitePurchaseOrderRequestForm.helpers';
import { useNetSuitePurchaseOrderFields } from './NetSuitePurchaseOrderRequestForm.hooks';
import { messages } from './NetSuitePurchaseOrderRequestForm.messages';
import {
    netSuitePurchaseOrderFieldValuesState,
    netSuiteSendToSupplierState,
} from './NetSuitePurchaseOrderRequestForm.states';
import { NetSuitePurchaseOrderRequestFormProps } from './NetSuitePurchaseOrderRequestForm.types';

export const NetSuitePurchaseOrderRequestForm: FC<NetSuitePurchaseOrderRequestFormProps> = memo((props) => {
    const { request } = props;

    const shouldPrefillEditFormRef = useRef<boolean>(true);

    const pageContext = useSelector(getContext);

    const netSuiteEditingContext = useSelector(getNetSuiteContext);

    const canCreateContact = useSelector((state) => getCanCreateContact(state, request));

    const fieldValues = useAtomValue(netSuitePurchaseOrderFieldValuesState);
    const [isEmailToSupplierEnabled, setEmailToSupplierEnabled] = useAtom(netSuiteSendToSupplierState);
    const [showErrors, setShowErrors] = useAtom(netSuiteShowErrorsState);

    const customFieldsValues = useAtomValue(netSuiteCustomFieldsState);
    const expenseItems = useAtomValue(netSuiteExpenseItemsState);
    const itemLines = useAtomValue(netSuiteItemLinesState);
    const setNextLineNumber = useSetAtom(netSuiteItemLineNumberState);

    const resetPurchaseOrderFormState = useResetPurchaseOrderFormState();

    const {
        currencies,
        terms,
        items: editingContextItems,
        expenseCategories,
        availableLineItemType,
        customFields,
        employees,
        supplierEmailSettings,
        documentFields,
    } = netSuiteEditingContext;

    const { updateFieldsBulk } = useNetSuitePurchaseOrderFields();

    const { totalAmount, taxAmount, netAmount } = useCalculatedAmount(fieldValues.currency);

    const currentUser = useCurrentUser();

    const { details } = request;

    const isDraft = request.statusV2 === domain.RequestStatusV2.Draft;

    const { setCustomFieldValuesBulk } = useCustomFields(customFields);

    const { onUploadAttachments } = useUploadAttachments(request.id);

    const { setExpenseItems, updateExpenseItemsAccount, updateExpenseItemsOnCurrencyChange } = useExpenseItems(
        fieldValues.currency ?? details.currency
    );

    const { setItemLines, updateItemsOnCurrencyChange } = useItemLines(fieldValues.currency ?? details.currency);

    const {
        data: emailToSupplierData,
        setEmailToSupplierData,
        setAttachments: setEmailToSupplierAttachments,
        removeAttachment: removeEmailToSupplierAttachment,
    } = useEmailToSupplier(details.emailToSupplier || null, currentUser, isDraft, supplierEmailSettings);

    const fieldSettings = useNetSuitePurchaseOrderFieldSettings(documentFields);
    const expensesFieldSettings = useNetSuiteExpensesFieldSettings(documentFields);
    const itemsFieldSettings = useNetSuiteItemsFieldSettings(documentFields);
    const attachmentsFieldSettings = useNetSuiteAttachmentsFieldSettings(documentFields);

    const { data: exchangeRatesData } = useExchangeRates(request.companyId, fieldValues.date);

    const [isVendorPopupOpened, setIsVendorPopupOpened] = useState(false);

    const defaultCurrencyRate = useMemo(() => {
        const exchangeRate = exchangeRatesData?.rates.find((currency) => {
            return currency.id === fieldValues.currency?.id;
        });

        return exchangeRate?.rateValue;
    }, [exchangeRatesData, fieldValues.currency]);

    useMount(() => {
        const isEnabledByDefault =
            details.emailToSupplier?.emailHasToBeSent ||
            supplierEmailSettings?.state === domain.TemplateSettingsEmailToSupplierState.EnabledAndActive;

        setEmailToSupplierEnabled(isEnabledByDefault);
    });

    useUnmount(() => {
        resetPurchaseOrderFormState();
    });

    useEffect(() => {
        if (shouldPrefillEditFormRef.current) {
            const { itemLines, expenseLines, ...fields } = request.details;

            const date = fields.transactionDate || moment().toISOString();

            const currentUserFullName = `${currentUser.firstName} ${currentUser.lastName}`.trim().toLowerCase();

            const defaultEmployee = employees.find((employee) => {
                return employee.text.toLowerCase() === currentUserFullName;
            });

            updateFieldsBulk(
                {
                    ...fields,
                    vendor: fields.vendor || undefined,
                    date,
                    employee: fields.employee || defaultEmployee || null,
                    address: mapAddress(fields.address),
                    shippingAddress: fields.shippingAddress
                        ? mapAddress({
                              id: fields.shippingAddress.id,
                              fullAddress: fields.shippingAddress.text,
                          })
                        : null,
                    exchangeRate:
                        request.exchangeRateSource === domain.ExchangeRateSource.Manual ? fields.exchangeRate : null,
                },
                false
            );

            const expandedExpenseLines = expenseLines.map((expenseLine) => {
                const expenseCategory = expenseCategories.find((category) => category.id === expenseLine.category?.id);

                if (expenseCategory) {
                    return {
                        ...expenseLine,
                        rates: expenseCategory.rates,
                    };
                }

                return expenseLine;
            });

            const expandedItemLines = itemLines.map((itemLine) => {
                const itemInfo = editingContextItems.find(
                    (editingContextItem) => editingContextItem.id === itemLine.item?.id
                );

                if (itemInfo) {
                    return {
                        ...itemLine,
                        unitList: itemInfo.unitList || [],
                    };
                }

                return itemLine;
            });

            if (itemLines.length > 0 || expenseLines.length > 0) {
                const maxLineNumber = Math.max(
                    ...[
                        ...itemLines.map((item) => item.lineNumber),
                        ...expenseLines.map((expense) => expense.lineNumber),
                    ]
                );

                setNextLineNumber(maxLineNumber + 1);
            }

            setExpenseItems(expandedExpenseLines.length > 0 ? expandedExpenseLines : []);
            setItemLines(expandedItemLines.length > 0 ? expandedItemLines : []);

            if (fields.customFields) {
                setCustomFieldValuesBulk(
                    fields.customFields.length > 0 ? convertCustomFieldsToState(fields.customFields) : {}
                );
            }

            shouldPrefillEditFormRef.current = false;
        }
    }, [
        request,
        terms,
        editingContextItems,
        expenseCategories,
        employees,
        currentUser,
        updateFieldsBulk,
        setExpenseItems,
        setItemLines,
        setNextLineNumber,
        setCustomFieldValuesBulk,
    ]);

    const getRequestTransfer = (): backend.Transfer<backend.transfers.RequestEditTransfer> => {
        const transfer: backend.Transfer<backend.transfers.RequestEditTransfer> = {
            requestId: request.id,
            amount: totalAmount || null,
            currency: currencies.find((currency) => currency.id === fieldValues.currency?.id)?.ISOCode,
            exchangeRate: fieldValues.exchangeRate || defaultCurrencyRate || 1,
            exchangeRateSource:
                typeof fieldValues.exchangeRate === 'number'
                    ? domain.ExchangeRateSource.Manual
                    : domain.ExchangeRateSource.NetSuite,
            exchangeRateDate: isFutureDate(fieldValues.date)
                ? moment.utc().format('yyyy-MM-DD')
                : moment.utc(fieldValues.date).format('yyyy-MM-DD'),
            attachments: request.attachments.map((attachment) => attachment.id),
            requestNote: request.requestNote.value,
            templateVersion: pageContext.templateVersion,
            companyId: request.companyId,
        };

        const emailsToContactTo =
            emailToSupplierData?.to
                .map((toEmailString) => toEmailString.split(','))
                .flat()
                .map((toEmail) => stringHelpers.parseEmail(toEmail))
                .filter((toEmail) => toEmail) || [];

        const emailToContactTo = uniqWith(
            emailsToContactTo,
            (a, b) => a?.toLocaleLowerCase() === b?.toLocaleLowerCase()
        ).join(',');

        const emailsToContactCC =
            emailToSupplierData?.cc
                .map((ccEmailString) => ccEmailString.split(','))
                .flat()
                .map((ccEmail) => stringHelpers.parseEmail(ccEmail))
                .filter(
                    (ccEmail) => ccEmail && !emailToContactTo.toLocaleLowerCase().includes(ccEmail.toLocaleLowerCase())
                ) || [];

        const emailToContactCC = uniqWith(
            emailsToContactCC,
            (a, b) => a?.toLocaleLowerCase() === b?.toLocaleLowerCase()
        ).join(',');

        let emailSubject = emailToSupplierHelpers.removeRootParagraph(emailToSupplierData?.subject || '');

        emailSubject = emailToSupplierHelpers.cleanupSubjectString(emailSubject);
        emailSubject = emailToSupplierHelpers.returnExpressions(emailSubject);

        transfer.emailToContact = {
            emailHasToBeSent: isEmailToSupplierEnabled,
            emailTo: emailToContactTo,
            emailCC: emailToContactCC,
            emailFrom: emailToSupplierData?.from || '',
            emailReplyTo: emailToSupplierData?.replyTo || '',
            emailSubject: emailToSupplierHelpers.removeMentionExtensionTags(emailSubject),
            emailBody: emailToSupplierHelpers.removeMentionExtensionTags(emailToSupplierData?.body || ''),
            attachments: emailToSupplierData?.attachments.map((a) => a.id) || [],
        };

        transfer.netSuitePurchaseOrderDetails = {
            vendorReference: fieldValues.vendorReference,
            vendorId: fieldValues.vendor?.id || null,
            dueDate: fieldValues.dueDate || null,
            transactionDate: fieldValues.date || null,
            reference: fieldValues.reference || null,
            classId: fieldValues.class?.id || null,
            locationId: fieldValues.location?.id || null,
            departmentId: fieldValues.department?.id || null,
            currencyId: fieldValues.currency?.id || null,
            originalExchangeRate: fieldValues.exchangeRate || defaultCurrencyRate || null,
            billingAddressId: fieldValues.address?.id ?? null,
            subtotal: null,
            taxTotal: taxAmount,
            total: netAmount,
            memo: fieldValues.memo || null,
            employeeId: fieldValues.employee?.id ?? null,
            termId: fieldValues.terms?.id ?? null,
            incotermName: fieldValues.incoterm?.text ?? null,
            shipToId: fieldValues.shipTo?.id ?? null,
            shippingAddressId: fieldValues.shippingAddress?.id ?? null,
            shipDate: fieldValues.shipDate ?? null,
            shipMethodId: fieldValues.shipMethod?.id ?? null,
            freeOnBoard: fieldValues.freeOnBoard ?? null,
            trackingNumbers: fieldValues.trackingNumbers ?? null,
            customFields: getCustomFieldsTransfer(Object.values(customFieldsValues)),
            ...getExpenseAndItemLineTransfers(domain.IntegrationCode.NetSuitePO, isDraft),
        };

        return transfer;
    };

    const { vendorData, isLoading: isVendorDataLoading } = useVendorData(
        currencies,
        fieldValues.vendor?.id,
        request.id,
        request.companyId
    );

    const prevVendorData = usePrevious(vendorData);

    const showExpensesTable =
        availableLineItemType === domain.NetSuiteAvailableLineItemType.ExpenseLines ||
        availableLineItemType === domain.NetSuiteAvailableLineItemType.All;

    const showItemsTable =
        availableLineItemType === domain.NetSuiteAvailableLineItemType.ItemLines ||
        availableLineItemType === domain.NetSuiteAvailableLineItemType.All;

    const isEmailToSupplierSectionEnabled =
        supplierEmailSettings?.state !== domain.TemplateSettingsEmailToSupplierState.Disabled;

    useEffect(() => {
        /**
         * Do not update fields with default values from vendor data
         * if it was first fetch for saved vendor
         * as in this case prefilled fields from request
         * will be rewritten
         */
        if (!prevVendorData && vendorData && vendorData.id === request.details.vendor?.id) {
            return;
        }

        if (prevVendorData?.id === vendorData?.id) {
            return;
        }

        if (vendorData) {
            updateFieldsBulk({
                address: vendorData.defaultBillingAddress,
                currency: vendorData.currency,
                terms: vendorData.terms,
                incoterm: vendorData.incoterm,
            });

            showExpensesTable && updateExpenseItemsAccount(vendorData.defaultExpenseAccount);

            if (vendorData.email) {
                setEmailToSupplierData([vendorData.email], 'to');
            }
        }
    }, [
        vendorData,
        updateFieldsBulk,
        setEmailToSupplierData,
        prevVendorData,
        request.details.vendor?.id,
        showExpensesTable,
        updateExpenseItemsAccount,
    ]);

    const requestCurrencyISOCode = useMemo(() => {
        return currencies.find((currency) => currency.id === fieldValues.currency?.id)?.ISOCode ?? null;
    }, [currencies, fieldValues.currency]);

    const getValidationErrors = async () => {
        setShowErrors(true);

        return getPurchaseOrderValidationErrors({
            fields: fieldValues,
            customFieldsValues: customFieldsValues,
            customFieldsList: customFields,
            workflowId: request.template.id,
            expenseLines: expenseItems,
            itemLines,
            totalAmount,
            taxAmount,
            isEmailToSupplierEnabled,
            emailToSupplierData,
            defaultRate: defaultCurrencyRate,
            fieldSettings,
            expensesFieldSettings,
            itemsFieldSettings,
            attachmentsFieldSettings,
            attachments: request.attachments,
            companyCurrency: request.companyCurrency,
            requestCurrencyISOCode,
        });
    };

    const closeVendorPopup = useCallback(() => {
        setIsVendorPopupOpened(false);
    }, []);

    const onNewVendorSave = useCallback(
        (vendor: CreateNetSuiteVendor['response']['vendor']) => {
            updateFieldsBulk({
                vendor: {
                    id: vendor.id,
                    text: vendor.name,
                },
                vatRegistrationNumber: vendor.taxNumber,
                address: null,
                currency: null,
            });

            closeVendorPopup();
        },
        [updateFieldsBulk, closeVendorPopup]
    );

    const onNewVendorClick = useCallback(() => {
        setIsVendorPopupOpened(true);

        return Promise.resolve(null);
    }, []);

    const {
        isBelgradeUIFilesUploadSection,
        isBelgradeUIEmailToSupplier,
        isBelgradeUILineItemsTotalSection,
        isBelgradeUINetSuiteVendorCreationPopup,
    } = useUserBetaFeatures();

    return (
        <ConditionWrapper
            condition={!isBelgradeUIFilesUploadSection && !isBelgradeUIEmailToSupplier}
            wrapperIf={(children) => (
                <FileDropProvider maxSize={pageContext.attachmentMaxSize} onUpload={onUploadAttachments}>
                    {children}
                </FileDropProvider>
            )}
        >
            <Root>
                <Toolbar>
                    <ToolbarText>{messages.toolbarText}</ToolbarText>

                    <ActionsBlock
                        customGetRequestTransfer={getRequestTransfer}
                        customRequestValidationFn={getValidationErrors}
                    />
                </Toolbar>

                <Content>
                    <ContentInner>
                        <RequesterInstructionSection instructionText={pageContext.requesterInstruction} />

                        <FieldsSection
                            requestId={request.id}
                            companyId={request.companyId}
                            workflowId={request.template.id}
                            vendorData={vendorData}
                            isVendorDataLoading={isVendorDataLoading}
                            canCreateVendor={canCreateContact}
                            initialMemoValue={details.memo}
                            onNewVendorClick={onNewVendorClick}
                            updateItemsOnCurrencyChange={updateItemsOnCurrencyChange}
                            updateExpenseItemsOnCurrencyChange={updateExpenseItemsOnCurrencyChange}
                        />

                        {showExpensesTable && (
                            <NetSuiteExpenses
                                currency={fieldValues.currency}
                                integrationCode={domain.IntegrationCode.NetSuitePO}
                                workflowId={request.template.id}
                            />
                        )}

                        {showItemsTable && (
                            <NetSuiteItems
                                currency={fieldValues.currency}
                                integrationCode={domain.IntegrationCode.NetSuitePO}
                                workflowId={request.template.id}
                            />
                        )}

                        {isBelgradeUILineItemsTotalSection ? (
                            <NetSuiteRequestItemsSummary
                                netAmount={netAmount}
                                taxAmount={taxAmount}
                                totalAmount={totalAmount}
                                currency={fieldValues.currency}
                            />
                        ) : (
                            <NetSuiteRequestFooterLegacy
                                netAmount={netAmount}
                                taxAmount={taxAmount}
                                totalAmount={totalAmount}
                                currency={fieldValues.currency}
                            />
                        )}

                        <ShippingDetailsSection companyId={request.companyId} />

                        {isFieldVisible(attachmentsFieldSettings.attachments) &&
                            (isBelgradeUIFilesUploadSection ? (
                                <Grid gridTemplateColumns='50%' gap={16}>
                                    <AttachmentsSection
                                        request={request}
                                        required={isFieldMandatory(attachmentsFieldSettings.attachments)}
                                    />
                                </Grid>
                            ) : (
                                <FilesSection required={isFieldMandatory(attachmentsFieldSettings.attachments)} />
                            ))}

                        {isEmailToSupplierSectionEnabled && (
                            <NetSuiteEmailToSupplier
                                data={emailToSupplierData}
                                vendorEmail={vendorData?.email}
                                isEnabled={isEmailToSupplierEnabled}
                                showErrors={showErrors}
                                setData={setEmailToSupplierData}
                                setIsEnabled={setEmailToSupplierEnabled}
                                setAttachments={setEmailToSupplierAttachments}
                                removeAttachment={removeEmailToSupplierAttachment}
                            />
                        )}
                    </ContentInner>
                </Content>

                {isBelgradeUINetSuiteVendorCreationPopup ? (
                    <Popup open={isVendorPopupOpened} onClose={closeVendorPopup} size='large'>
                        <NetSuiteVendorCreationPopupContent
                            terms={netSuiteEditingContext.terms || []}
                            currencies={netSuiteEditingContext.currencies || []}
                            requestSubsidiary={request.details.subsidiary}
                            onSave={onNewVendorSave}
                        />
                    </Popup>
                ) : (
                    <PopupOld isOpen={isVendorPopupOpened} onRequestClose={closeVendorPopup}>
                        <NetSuiteVendorCreationPopup
                            companyId={request.companyId}
                            terms={netSuiteEditingContext.terms || []}
                            currencies={netSuiteEditingContext.currencies || []}
                            requestSubsidiary={request.details.subsidiary}
                            onSave={onNewVendorSave}
                        />
                    </PopupOld>
                )}
            </Root>
        </ConditionWrapper>
    );
});

NetSuitePurchaseOrderRequestForm.displayName = 'NetSuitePurchaseOrderRequestForm';
